// feature/notifications-history.
// Per Molly's request 2026-05-07: the bell dropdown only surfaces recent /
// unread notifications, but she needs a way to look back at everything she's
// received — especially insumos-related — to make sure nothing slips
// through.
//
// Dedicated page at /notificaciones with three filter axes:
//   • time pills: 7 / 30 / 90 / 365 days (default 30)
//   • category tabs: Todas / Insumos / Fallos / Casos / Sistema
//   • only-unread toggle (uses notifications.read_at)
// Reuses Sprint 58 HistorialTab visual pattern: grouped by day, action
// icons, time pills. Bell dropdown gains a "Ver todas" link at the bottom.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { notifications as notifApi } from "../api/endpoints";
import Spinner from "../components/Spinner";
import Empty from "../components/Empty";
import { TipoPill, EstadoPill, TipoFalloPill } from "../components/Pills";
import { useToast } from "../components/Toast";

// Mirrors TopBar's NOTIF_TYPE_ICONS — kept in sync manually so the bell
// dropdown and the history page render the same icon for any given type.
// New types added to either map should be mirrored.
const NOTIF_TYPE_ICONS = {
  "case.insumo_received":              "📩",
  "case_returned":                     "🔁",
  "case.devuelto_resuelto":            "🔁",
  "case.contestada_stop_work":         "🛑",
  "case.moderation_requested":         "⚠️",
  "case.moderation_resolved":          "✅",
  "case.sancion_inaplicacion":         "🚨",
  "case.marked_contestada":            "📨",
  "case.pendiente_archivar":           "📁",
  "case.archivado":                    "📦",
  "case.sent_back_to_lawyer":          "↩️",
  "case.fallo_routed_to_you":          "📋",
  "case.fallo_routed_to_molly":        "📋",
  "case.timer2_started":               "⏰",
  "case.esperando_insumo":             "⏰",
  "case.sla_resumed":                  "▶️",
  "case_assigned":                     "⚖️",
  "case_created":                      "🆕",
  "insumo_assigned":                   "📋",
  "insumo_response":                   "📝",
  "insumo_stolen":                     "↪️",
  "audit_pending":                     "🔍",
  "audit_approved":                    "✅",
  "audit_rejected":                    "❌",
  "fallo_pending":                     "📂",
  "system.workflow_change":            "🔔",
  "system.bulk_fallos_routed":         "📦",
  "system.bulk_desfavorables_restored":"📦",
  "system.test_cleanup":               "🧹",
  "system.maintenance":                "🛠️",
};

const TIME_OPTIONS = [
  { days: 7,   label: "7 días" },
  { days: 30,  label: "30 días" },
  { days: 90,  label: "90 días" },
  { days: 365, label: "Todas" },
];

const CATEGORIES = [
  { key: "all",      label: "Todas",   icon: "🔔" },
  { key: "insumos",  label: "Insumos", icon: "📋" },
  { key: "fallos",   label: "Fallos",  icon: "📂" },
  { key: "casos",    label: "Casos",   icon: "⚖️" },
  { key: "sistema",  label: "Sistema", icon: "🛠️" },
];

const DIAS_ES  = ["domingo","lunes","martes","miércoles","jueves","viernes","sábado"];
const MESES_ES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

function bogotaDateKey(d) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Bogota",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date(d));
}

function dayLabel(yyyyMmDd) {
  const today     = bogotaDateKey(new Date());
  const yesterday = bogotaDateKey(new Date(Date.now() - 86400000));
  if (yyyyMmDd === today)     return "Hoy";
  if (yyyyMmDd === yesterday) return "Ayer";
  const [y, m, d] = yyyyMmDd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 17, 0, 0));
  const wd = new Intl.DateTimeFormat("en-US", { timeZone: "America/Bogota", weekday: "long" })
    .format(dt).toLowerCase();
  const wdIdx = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"].indexOf(wd);
  return `${DIAS_ES[wdIdx]}, ${d} de ${MESES_ES[m - 1]}`;
}

function relativeTime(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1)  return "ahora";
  if (min < 60) return `hace ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24)  return `hace ${hr} h`;
  const d = Math.floor(hr / 24);
  if (d < 30)   return `hace ${d} d`;
  return new Date(iso).toLocaleDateString("es-CO", { day: "2-digit", month: "short" });
}

function absoluteTime(iso) {
  return new Date(iso).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" });
}

export default function NotificationsHistory() {
  const toast = useToast();
  const [days, setDays]           = useState(30);
  const [category, setCategory]   = useState("all");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [data, setData]           = useState(null);
  const [marking, setMarking]     = useState(false);

  function load() {
    setData(null);
    const params = { days, category, limit: 200 };
    if (onlyUnread) params.only_unread = 1;
    notifApi.history(params)
      .then((r) => setData(r.data))
      .catch(() => setData({ notifications: [], unread_total: 0 }));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days, category, onlyUnread]);

  async function markOne(id) {
    // Optimistic
    setData((d) => d ? {
      ...d,
      notifications: d.notifications.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n),
      unread_total: Math.max(0, (d.unread_total || 0) - 1),
    } : d);
    try { await notifApi.read(id); }
    catch { toast.push("No se pudo marcar como leída", "error"); load(); }
  }

  async function markAll() {
    setMarking(true);
    const ts = new Date().toISOString();
    setData((d) => d ? {
      ...d,
      notifications: d.notifications.map((n) => n.read_at ? n : { ...n, read_at: ts }),
      unread_total: 0,
    } : d);
    try { await notifApi.readAll(); }
    catch { toast.push("No se pudo marcar todas como leídas", "error"); load(); }
    finally { setMarking(false); }
  }

  const grouped = useMemo(() => {
    if (!data?.notifications) return {};
    const buckets = {};
    for (const n of data.notifications) {
      const key = bogotaDateKey(n.created_at);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(n);
    }
    return buckets;
  }, [data]);

  const orderedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));
  const total       = data?.notifications?.length ?? 0;
  const unread      = data?.unread_total ?? 0;

  return (
    <div className="col" style={{ gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0 }}>Notificaciones</h1>
          <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
            Historial completo. {unread > 0 && <strong>{unread} sin leer.</strong>}
          </div>
        </div>
        {unread > 0 && (
          <button className="secondary" onClick={markAll} disabled={marking}>
            {marking ? "Marcando…" : "✓ Marcar todas como leídas"}
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
        <div role="tablist" style={{
          display: "flex", gap: 4, flexWrap: "wrap",
          borderBottom: "1px solid var(--border-hairline)",
        }}>
          {CATEGORIES.map((c) => (
            <CategoryTab key={c.key} {...c}
                         active={category === c.key}
                         onClick={() => setCategory(c.key)} />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span className="muted" style={{ fontSize: 12, textTransform: "none", letterSpacing: 0 }}>Periodo:</span>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {TIME_OPTIONS.map((o) => (
              <button key={o.days} type="button"
                      className={days === o.days ? "" : "secondary"}
                      onClick={() => setDays(o.days)}
                      style={{ padding: "4px 12px", fontSize: 12 }}>
                {o.label}
              </button>
            ))}
          </div>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, marginLeft: "auto" }}>
            <input type="checkbox" checked={onlyUnread}
                   onChange={(e) => setOnlyUnread(e.target.checked)} />
            Solo no leídas
          </label>
        </div>
      </div>

      {data == null ? (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      ) : total === 0 ? (
        <div className="card">
          <Empty
            title="Sin notificaciones en este filtro"
            message="Cambie el periodo o la categoría para ver más eventos."
          />
        </div>
      ) : (
        <div className="col" style={{ gap: 16 }}>
          {orderedKeys.map((key) => (
            <section key={key}>
              <div className="muted" style={{
                fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5,
                fontWeight: 600, marginBottom: 6, paddingBottom: 4,
                borderBottom: "1px solid var(--border-hairline)",
              }}>
                {dayLabel(key)} <span style={{ marginLeft: 6, opacity: 0.7 }}>({grouped[key].length})</span>
              </div>
              <div className="col" style={{ gap: 6 }}>
                {grouped[key].map((n) => (
                  <NotifRow key={n.id} n={n} onMarkRead={markOne} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryTab({ label, icon, active, onClick }) {
  return (
    <button type="button" role="tab" aria-selected={active}
            onClick={onClick}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "10px 16px",
              background: "transparent", border: "none",
              borderBottom: `2px solid ${active ? "var(--accent-mint)" : "transparent"}`,
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active ? "var(--accent-mint)" : "var(--text-muted)",
              cursor: "pointer",
              marginBottom: -1,
            }}>
      <span aria-hidden="true">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function NotifRow({ n, onMarkRead }) {
  const isUnread = n.read_at == null;
  const icon = NOTIF_TYPE_ICONS[n.type] || "🔔";
  return (
    <div className="card" style={{
      padding: 12,
      display: "flex", alignItems: "center", gap: 12,
      borderLeft: `3px solid ${isUnread ? "var(--accent-mint)" : "transparent"}`,
      opacity: isUnread ? 1 : 0.78,
    }}>
      <div style={{ fontSize: 18, lineHeight: 1, flexShrink: 0, width: 28, textAlign: "center" }}
           aria-hidden="true">{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>
            {n.message}
          </span>
        </div>
        {n.case_id && (
          <div style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <Link to={`/cases?open=${n.case_id}`}
                  style={{ color: "var(--accent-mint)", textDecoration: "none", fontWeight: 500 }}>
              {n.case_number || `Caso #${n.case_id}`} ↗
            </Link>
            {n.tipo && <TipoPill tipo={n.tipo} />}
            {n.tipo === "fallo" && n.tipo_fallo && <TipoFalloPill value={n.tipo_fallo} />}
            {n.case_estado && <EstadoPill c={{ estado: n.case_estado }} />}
            {n.accionante && <span className="muted">· {n.accionante}</span>}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
          {relativeTime(n.created_at)}
        </span>
        <span className="muted" style={{ fontSize: 11 }}>{absoluteTime(n.created_at)}</span>
        {isUnread && (
          <button className="secondary sm" style={{ padding: "2px 8px", fontSize: 11 }}
                  onClick={() => onMarkRead(n.id)}
                  title="Marcar como leída">
            ✓ Leída
          </button>
        )}
      </div>
    </div>
  );
}
